import React, { useEffect, useState, useRef } from 'react';
import { RoomMetadata, RoomMode } from '@tankworld/shared';
import { useGameStore } from '../../state/game-store.js';
import { networkClient } from '../../network/network-client.js';
import { soundSystem } from '../../audio/sound-system.js';
import {
  Users,
  Plus,
  RefreshCw,
  ArrowLeft,
  Play,
  ShieldAlert,
  Key,
  Copy,
  Check,
  MessageSquare,
  Send,
  Crown,
  UserCheck,
  UserX,
  Shield,
  Search,
  Clock,
  Radio,
} from 'lucide-react';

interface MultiplayerLobbyProps {
  onBack: () => void;
}

export const MultiplayerLobby: React.FC<MultiplayerLobbyProps> = ({ onBack }) => {
  const setGameMode = useGameStore((s) => s.setGameMode);
  const activeRoom = useGameStore((s) => s.activeRoom);
  const setActiveRoom = useGameStore((s) => s.setActiveRoom);
  const localPlayerId = useGameStore((s) => s.localPlayerId);
  const countdownRemaining = useGameStore((s) => s.countdownRemaining);
  const chatMessages = useGameStore((s) => s.chatMessages);
  const pingMs = useGameStore((s) => s.pingMs);

  // Browser state
  const [rooms, setRooms] = useState<RoomMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Filters & Search
  const [searchCode, setSearchCode] = useState('');
  const [filterMode, setFilterMode] = useState<string>('all');
  const [filterMap, setFilterMap] = useState<string>('all');

  // Create Room modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRoomName, setNewRoomName] = useState('Frontline Battle');
  const [newRoomMode, setNewRoomMode] = useState<RoomMode>('ffa');
  const [newRoomMap, setNewRoomMap] = useState('proving_grounds');
  const [newRoomBots, setNewRoomBots] = useState(4);
  const [newRoomPrivate, setNewRoomPrivate] = useState(false);

  // Room view state
  const [chatInput, setChatInput] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const fetchRooms = async () => {
    setIsLoading(true);
    setErrorMsg(null);
    try {
      const res = await fetch('/api/v1/rooms');
      if (res.ok) {
        const data = await res.json();
        setRooms(data.rooms || []);
      } else {
        setErrorMsg('Không thể kết nối đến máy chủ phòng chờ.');
      }
    } catch {
      setErrorMsg('Máy chủ phòng chờ chưa sẵn sàng.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  const handleJoinRoom = async (roomIdOrCode?: string) => {
    soundSystem.playUiClick();
    setIsJoining(true);
    setErrorMsg(null);

    const success = await networkClient.connect(roomIdOrCode);
    if (!success) {
      setErrorMsg('Không thể kết nối WebSocket đến phòng này.');
    }
    setIsJoining(false);
  };

  const handleJoinByCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchCode.trim()) return;
    soundSystem.playUiClick();
    handleJoinRoom(searchCode.trim().toUpperCase());
  };

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    soundSystem.playUiClick();
    try {
      const res = await fetch('/api/v1/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newRoomName,
          mode: newRoomMode,
          mapId: newRoomMap,
          botCount: newRoomBots,
          isPrivate: newRoomPrivate,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setShowCreateModal(false);
        await handleJoinRoom(data.room.id);
      } else {
        setErrorMsg('Lỗi tạo phòng mới.');
      }
    } catch {
      setErrorMsg('Lỗi mạng khi tạo phòng.');
    }
  };

  const handleLeaveRoom = () => {
    soundSystem.playUiClick();
    networkClient.disconnect();
    setActiveRoom(null);
    fetchRooms();
  };

  const handleSendChat = (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    networkClient.sendChat(chatInput.trim());
    setChatInput('');
  };

  const handleCopyCode = () => {
    if (activeRoom?.roomCode) {
      navigator.clipboard.writeText(activeRoom.roomCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  // Filtered rooms
  const filteredRooms = rooms.filter((r) => {
    if (filterMode !== 'all' && r.mode !== filterMode) return false;
    if (filterMap !== 'all' && r.mapId !== filterMap) return false;
    return true;
  });

  const isHost = activeRoom?.hostId === localPlayerId;
  const localPlayer = activeRoom?.players?.find((p) => p.id === localPlayerId);

  // -------------------------------------------------------------
  // IN-ROOM WAITING / PREPARATION VIEW
  // -------------------------------------------------------------
  if (activeRoom) {
    return (
      <div className="absolute inset-0 z-30 flex flex-col justify-between p-6 sm:p-8 bg-zinc-950/95 font-mono overflow-y-auto">
        {/* Top Header */}
        <div className="flex flex-wrap justify-between items-center border-b border-zinc-800 pb-4 gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={handleLeaveRoom}
              className="p-2.5 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-700 transition-all"
              title="Rời phòng"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl sm:text-2xl font-bold tracking-wider text-zinc-100 uppercase">
                  {activeRoom.name}
                </h2>
                <span
                  className={`text-xs px-2.5 py-0.5 rounded uppercase font-bold border ${
                    activeRoom.status === 'countdown'
                      ? 'bg-amber-950/80 border-amber-500 text-amber-300 animate-pulse'
                      : activeRoom.status === 'in_game'
                      ? 'bg-emerald-950/80 border-emerald-500 text-emerald-300'
                      : 'bg-zinc-800 border-zinc-600 text-zinc-300'
                  }`}
                >
                  {activeRoom.status}
                </span>
              </div>
              <div className="text-xs text-zinc-400 mt-1 flex items-center gap-3">
                <span>Bản đồ: <b className="text-zinc-200 capitalize">{activeRoom.mapId.replace('_', ' ')}</b></span>
                <span>Chế độ: <b className="text-zinc-200 uppercase">{activeRoom.mode}</b></span>
                <span className="flex items-center gap-1">
                  <Radio className="w-3 h-3 text-emerald-400 animate-pulse" /> Ping: {pingMs}ms
                </span>
              </div>
            </div>
          </div>

          {/* Room Code Badge */}
          <div className="flex items-center gap-3">
            <div className="hud-panel px-3.5 py-1.5 rounded border border-emerald-700/60 flex items-center gap-2 bg-zinc-900/90">
              <span className="text-[11px] text-zinc-400 uppercase font-mono">Mã phòng:</span>
              <span className="text-sm font-bold tracking-widest text-emerald-400">{activeRoom.roomCode}</span>
              <button
                onClick={handleCopyCode}
                className="ml-1 p-1 text-zinc-400 hover:text-zinc-100 transition-colors"
                title="Sao chép mã"
              >
                {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>

            <button
              onClick={handleLeaveRoom}
              className="px-4 py-2 bg-zinc-900 hover:bg-rose-950/80 border border-zinc-700 hover:border-rose-600 text-zinc-300 hover:text-rose-300 rounded text-xs font-bold uppercase transition-all"
            >
              Rời phòng
            </button>
          </div>
        </div>

        {/* Synchronized Countdown Banner */}
        {countdownRemaining !== null && countdownRemaining > 0 && (
          <div className="my-4 p-4 rounded-lg bg-gradient-to-r from-amber-950/90 via-amber-900/80 to-amber-950/90 border border-amber-500/80 shadow-lg shadow-amber-950/50 flex items-center justify-center gap-4 animate-bounce">
            <Clock className="w-6 h-6 text-amber-400 animate-spin" />
            <span className="text-lg sm:text-xl font-extrabold tracking-widest text-amber-200 uppercase">
              BẮT ĐẦU TRẬN ĐẤU TRONG: {countdownRemaining} GIÂY!
            </span>
          </div>
        )}

        {/* Central Room Layout: Players List & Live Chat */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 my-auto py-4">
          {/* Left 2 Cols: Players List & Tactical Controls */}
          <div className="lg:col-span-2 space-y-4">
            <div className="hud-panel p-5 rounded-lg border border-emerald-900/40 space-y-4">
              <div className="flex justify-between items-center border-b border-zinc-800 pb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Danh sách chỉ huy ({activeRoom.players?.length || 0} / {activeRoom.maxPlayers})
                </span>
                <span className="text-xs text-zinc-400">
                  {isHost ? '★ Bạn là chủ phòng' : 'Chờ chủ phòng bắt đầu trận'}
                </span>
              </div>

              {/* Commander Slots */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {activeRoom.players?.map((p) => {
                  const isCurrent = p.id === localPlayerId;
                  return (
                    <div
                      key={p.id}
                      className={`p-3.5 rounded border flex flex-col justify-between transition-all ${
                        isCurrent
                          ? 'bg-emerald-950/40 border-emerald-500/80'
                          : 'bg-zinc-900/60 border-zinc-800'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2.5">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shadow-inner"
                            style={{ backgroundColor: p.color, color: '#fff' }}
                          >
                            {p.name.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 font-bold text-sm text-zinc-100">
                              {p.name}
                              {p.isHost && <Crown className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />}
                            </div>
                            <div className="text-[10px] text-zinc-400">
                              {p.rank} &bull; Ping: {p.pingMs}ms
                            </div>
                          </div>
                        </div>

                        <div className="text-right">
                          <span
                            className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded font-bold uppercase ${
                              p.isReady
                                ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-700/80'
                                : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                            }`}
                          >
                            {p.isReady ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                            {p.isReady ? 'Sẵn sàng' : 'Chờ'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 pt-2 border-t border-zinc-800/80 flex justify-between items-center text-[11px] text-zinc-400">
                        <span>Xe: <b className="text-zinc-200">{p.tankClassId.replace('_', ' ')}</b></span>
                        <span className="capitalize">
                          Đội: <b className={p.team === 'red' ? 'text-rose-400' : p.team === 'blue' ? 'text-blue-400' : 'text-zinc-400'}>{p.team}</b>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Player Self Controls */}
              <div className="pt-4 border-t border-zinc-800 flex flex-wrap justify-between items-center gap-3">
                <div className="flex items-center gap-3">
                  {/* Readiness Toggle */}
                  <button
                    onClick={() => {
                      soundSystem.playUiClick();
                      networkClient.setReady(!localPlayer?.isReady);
                    }}
                    className={`px-5 py-2.5 rounded font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${
                      localPlayer?.isReady
                        ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-600'
                        : 'bg-emerald-600 hover:bg-emerald-500 text-zinc-950 shadow-md shadow-emerald-950/60'
                    }`}
                  >
                    {localPlayer?.isReady ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                    {localPlayer?.isReady ? 'HỦY SẴN SÀNG' : 'SẴN SÀNG TÁC CHIẾN'}
                  </button>

                  {/* Team Selector */}
                  <select
                    value={localPlayer?.team || 'none'}
                    onChange={(e) => networkClient.setTeam(e.target.value as any)}
                    className="p-2.5 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-200 focus:outline-none focus:border-emerald-500 font-mono"
                  >
                    <option value="none">Phe: Tự do (FFA)</option>
                    <option value="red">Phe Đỏ (Red Team)</option>
                    <option value="blue">Phe Xanh (Blue Team)</option>
                  </select>
                </div>

                {/* Host Start Match Button */}
                {isHost && (
                  <button
                    onClick={() => {
                      soundSystem.playUiClick();
                      networkClient.startMatch();
                    }}
                    className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-zinc-950 font-bold text-xs rounded uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-emerald-950/80 transition-all"
                  >
                    <Play className="w-4 h-4 fill-current" /> BẮT ĐẦU TRẬN ĐẤU
                  </button>
                )}
              </div>
            </div>

            {/* Host Settings Panel (if host) */}
            {isHost && (
              <div className="hud-panel p-4 rounded-lg border border-zinc-800 text-xs space-y-3">
                <div className="font-bold text-zinc-300 uppercase tracking-wider text-[11px] flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5 text-emerald-400" /> Cấu hình trận đấu (Quyền chủ phòng)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-zinc-400 block mb-1">Bản đồ:</label>
                    <select
                      value={activeRoom.mapId}
                      onChange={(e) => networkClient.updateRoomSettings({ mapId: e.target.value })}
                      className="w-full p-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100"
                    >
                      <option value="proving_grounds">Proving Grounds</option>
                      <option value="desert_outpost">Desert Outpost</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-zinc-400 block mb-1">Chế độ chơi:</label>
                    <select
                      value={activeRoom.mode}
                      onChange={(e) => networkClient.updateRoomSettings({ mode: e.target.value as any })}
                      className="w-full p-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100"
                    >
                      <option value="ffa">Free For All (FFA)</option>
                      <option value="team_deathmatch">Team Deathmatch</option>
                      <option value="domination">Domination</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-zinc-400 block mb-1">Số lượng Bot AI:</label>
                    <input
                      type="number"
                      min="0"
                      max="8"
                      value={activeRoom.playerCount ? 4 : 4}
                      onChange={(e) => networkClient.updateRoomSettings({ botCount: Number(e.target.value) })}
                      className="w-full p-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Col: Live Room Chat */}
          <div className="hud-panel p-5 rounded-lg border border-emerald-900/40 flex flex-col h-[420px]">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-400 border-b border-zinc-800 pb-2 mb-3">
              <MessageSquare className="w-4 h-4" /> Kênh liên lạc chiến thuật
            </div>

            <div className="flex-1 overflow-y-auto space-y-2.5 pr-1 text-xs">
              {chatMessages.map((chat) => (
                <div key={chat.id} className="p-2 rounded bg-zinc-900/50 border border-zinc-800/60">
                  <div className="flex justify-between items-baseline mb-0.5">
                    <span className="font-bold text-emerald-400 text-[11px]">{chat.senderName}</span>
                    <span className="text-[9px] text-zinc-500">
                      {new Date(chat.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="text-zinc-200 leading-snug break-words">{chat.message}</div>
                </div>
              ))}
              {chatMessages.length === 0 && (
                <div className="h-full flex items-center justify-center text-zinc-600 text-[11px]">
                  Chưa có tin nhắn nào trong phòng tác chiến.
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <form onSubmit={handleSendChat} className="mt-3 pt-3 border-t border-zinc-800 flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value.slice(0, 200))}
                placeholder="Nhập lệnh liên lạc..."
                className="flex-1 p-2 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-100 focus:outline-none focus:border-emerald-500 font-mono"
              />
              <button
                type="submit"
                className="p-2 bg-emerald-600 hover:bg-emerald-500 text-zinc-950 font-bold rounded transition-all"
                title="Gửi"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-zinc-500 border-t border-zinc-800 pt-3">
          Trạng thái phòng được đồng bộ tự động qua máy chủ WSS authoritative 30Hz
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // LOBBY BROWSER VIEW (List, Filter, Search, Quick Join, Create)
  // -------------------------------------------------------------
  return (
    <div className="absolute inset-0 z-30 flex flex-col justify-between p-6 sm:p-8 bg-zinc-950/95 font-mono overflow-y-auto">
      {/* Top Bar */}
      <div className="flex flex-wrap justify-between items-center border-b border-zinc-800 pb-4 gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              soundSystem.playUiClick();
              onBack();
            }}
            className="p-2.5 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-700 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold tracking-wider text-zinc-100 flex items-center gap-2">
              <Users className="w-6 h-6 text-emerald-500" /> SẢNH TÁC CHIẾN MULTIPLAYER
            </h2>
            <p className="text-xs text-zinc-400">Phòng chơi đa người thời gian thực với mô phỏng vật lý authoritative</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              soundSystem.playUiClick();
              fetchRooms();
            }}
            disabled={isLoading}
            className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-xs text-zinc-200 rounded flex items-center gap-2 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} /> Làm mới
          </button>

          <button
            onClick={() => {
              soundSystem.playUiClick();
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-zinc-950 font-bold text-xs rounded flex items-center gap-2 uppercase tracking-wider transition-all"
          >
            <Plus className="w-3.5 h-3.5" /> Tạo phòng
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-5xl mx-auto w-full my-auto py-6 space-y-5">
        {errorMsg && (
          <div className="p-3 bg-rose-950/50 border border-rose-700/60 text-rose-300 rounded text-xs flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-rose-400 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Quick Join & Room Code Search */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Quick Match Action Banner */}
          <div className="hud-panel p-5 rounded-lg border border-emerald-900/40 flex flex-col justify-between gap-4">
            <div>
              <div className="text-base font-bold text-zinc-100">Tham gia nhanh (Quick Match)</div>
              <div className="text-xs text-zinc-400 mt-1">
                Tự động kết nối tới phòng tác chiến mở có số lượng chỉ huy phù hợp nhất.
              </div>
            </div>

            <button
              onClick={() => handleJoinRoom()}
              disabled={isJoining}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-zinc-950 font-bold text-sm rounded flex items-center justify-center gap-2 uppercase tracking-wider transition-all disabled:opacity-50"
            >
              <Play className="w-4 h-4 fill-current" />
              {isJoining ? 'Đang kết nối...' : 'VÀO TRẬN NGAY'}
            </button>
          </div>

          {/* Join by Code */}
          <div className="hud-panel p-5 rounded-lg border border-emerald-900/40 flex flex-col justify-between gap-4">
            <div>
              <div className="text-base font-bold text-zinc-100 flex items-center gap-2">
                <Key className="w-4 h-4 text-emerald-400" /> Tham gia bằng mã phòng
              </div>
              <div className="text-xs text-zinc-400 mt-1">
                Nhập mã 6 ký tự để tham gia phòng riêng tư hoặc phòng được mời từ đồng đội.
              </div>
            </div>

            <form onSubmit={handleJoinByCode} className="flex gap-2">
              <input
                type="text"
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                placeholder="MÃ PHÒNG (VD: A7X9B2)"
                maxLength={8}
                className="flex-1 p-2.5 bg-zinc-900 border border-zinc-700 rounded text-xs font-bold tracking-widest text-emerald-400 uppercase focus:outline-none focus:border-emerald-500 font-mono"
              />
              <button
                type="submit"
                disabled={isJoining || !searchCode.trim()}
                className="px-5 py-2.5 bg-zinc-800 hover:bg-emerald-600 hover:text-zinc-950 text-zinc-200 border border-zinc-700 font-bold text-xs rounded uppercase tracking-wider transition-all disabled:opacity-50"
              >
                Tham gia
              </button>
            </form>
          </div>
        </div>

        {/* Filter Controls Bar */}
        <div className="hud-panel p-3.5 rounded-lg border border-zinc-800 flex flex-wrap justify-between items-center gap-3 text-xs">
          <div className="flex items-center gap-3">
            <span className="text-zinc-400 font-bold uppercase text-[11px] flex items-center gap-1.5">
              <Search className="w-3.5 h-3.5 text-emerald-400" /> Bộ lọc:
            </span>
            <select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              className="p-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-300 font-mono"
            >
              <option value="all">Tất cả chế độ</option>
              <option value="ffa">Free For All</option>
              <option value="team_deathmatch">Team Deathmatch</option>
              <option value="domination">Domination</option>
            </select>

            <select
              value={filterMap}
              onChange={(e) => setFilterMap(e.target.value)}
              className="p-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-300 font-mono"
            >
              <option value="all">Tất cả bản đồ</option>
              <option value="proving_grounds">Proving Grounds</option>
              <option value="desert_outpost">Desert Outpost</option>
            </select>
          </div>

          <div className="text-zinc-400 text-[11px]">
            Hiển thị <b>{filteredRooms.length}</b> phòng hoạt động
          </div>
        </div>

        {/* Room List Table */}
        <div className="hud-panel rounded-lg border border-emerald-900/40 overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-800 text-xs font-bold uppercase tracking-wider text-emerald-400 flex justify-between">
            <span>Danh sách phòng ({filteredRooms.length})</span>
            <span>Số chỉ huy &bull; Thao tác</span>
          </div>

          <div className="divide-y divide-zinc-800">
            {filteredRooms.map((room) => (
              <div
                key={room.id}
                className="p-4 flex flex-col sm:flex-row justify-between items-center gap-4 hover:bg-zinc-900/40 transition-all text-xs"
              >
                <div>
                  <div className="font-bold text-zinc-100 text-sm flex items-center gap-2">
                    {room.name}
                    {room.isPrivate && (
                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-950/80 border border-amber-700/80 text-amber-400 font-normal">
                        Riêng tư
                      </span>
                    )}
                  </div>
                  <div className="text-zinc-400 text-[11px] mt-1 flex flex-wrap gap-2.5">
                    <span>Mã: <b className="text-emerald-400 font-mono">{room.roomCode}</b></span>
                    <span>&bull;</span>
                    <span>Bản đồ: <b className="text-zinc-300 capitalize">{room.mapId.replace('_', ' ')}</b></span>
                    <span>&bull;</span>
                    <span>Chế độ: <b className="text-zinc-300 uppercase">{room.mode}</b></span>
                    <span>&bull;</span>
                    <span>Trạng thái: <b className="text-emerald-400 uppercase">{room.status}</b></span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-zinc-300 font-bold">
                    {room.playerCount} / {room.maxPlayers}
                  </span>

                  <button
                    onClick={() => handleJoinRoom(room.id)}
                    disabled={isJoining || room.playerCount >= room.maxPlayers || room.status === 'in_game'}
                    className="px-4 py-2 bg-zinc-800 hover:bg-emerald-600 hover:text-zinc-950 text-zinc-200 border border-zinc-700 rounded font-bold transition-all disabled:opacity-50"
                  >
                    {room.status === 'in_game' ? 'Đang đấu' : 'Vào phòng'}
                  </button>
                </div>
              </div>
            ))}

            {filteredRooms.length === 0 && !isLoading && (
              <div className="p-8 text-center text-zinc-500 text-xs">
                Chưa có phòng công khai nào phù hợp. Bạn có thể nhấn "Tạo phòng" để làm chủ phòng mới!
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Room Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="hud-panel max-w-md w-full p-6 rounded-lg border border-emerald-700/60 space-y-4">
            <h3 className="text-lg font-bold text-zinc-100 uppercase tracking-wider">Tạo phòng tác chiến mới</h3>

            <form onSubmit={handleCreateRoom} className="space-y-4 text-xs">
              <div>
                <label className="text-zinc-400 block mb-1">Tên phòng:</label>
                <input
                  type="text"
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  maxLength={32}
                  required
                  className="w-full p-2.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 focus:outline-none focus:border-emerald-500 font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 block mb-1">Bản đồ:</label>
                  <select
                    value={newRoomMap}
                    onChange={(e) => setNewRoomMap(e.target.value)}
                    className="w-full p-2.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 font-mono"
                  >
                    <option value="proving_grounds">Proving Grounds</option>
                    <option value="desert_outpost">Desert Outpost</option>
                  </select>
                </div>

                <div>
                  <label className="text-zinc-400 block mb-1">Chế độ chơi:</label>
                  <select
                    value={newRoomMode}
                    onChange={(e) => setNewRoomMode(e.target.value as any)}
                    className="w-full p-2.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 font-mono"
                  >
                    <option value="ffa">Free For All</option>
                    <option value="team_deathmatch">Team Deathmatch</option>
                    <option value="domination">Domination</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-zinc-400 block mb-1">Số lượng Bot AI hỗ trợ (0 - 8):</label>
                <input
                  type="number"
                  min="0"
                  max="8"
                  value={newRoomBots}
                  onChange={(e) => setNewRoomBots(Number(e.target.value))}
                  className="w-full p-2.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 focus:outline-none focus:border-emerald-500 font-mono"
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="chk-private"
                  checked={newRoomPrivate}
                  onChange={(e) => setNewRoomPrivate(e.target.checked)}
                  className="rounded bg-zinc-900 border-zinc-700 text-emerald-600 focus:ring-0"
                />
                <label htmlFor="chk-private" className="text-zinc-300 cursor-pointer">
                  Phòng riêng tư (Chỉ tham gia bằng mã phòng)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-zinc-800">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-300 font-bold"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-zinc-950 font-bold rounded uppercase tracking-wider"
                >
                  Khởi tạo phòng
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-center text-xs text-zinc-500">
        Hệ thống phòng WSS 30Hz &bull; State machine WAITING &rarr; PREPARING &rarr; COUNTDOWN &rarr; IN_GAME
      </div>
    </div>
  );
};
